import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'
import styled, { css } from 'styled-components'
import { Typography, Box, Tabs, Tab, Button } from '@material-ui/core'
import TabPanels from '../TabPanels'
import OpenBets from '../OpenBets'
import ContextConsumer from '../Context'
import {
  removeAllFromSlip,
  updateUserBalance,
  addToBetHistory,
  getReceiptNumber,
} from './utilities.js'
import Multiples from './Multiples'
import MessageSnackBar from './MessageSnackBar'
import BetTotals from './BetTotals'
import BrowserView from '../../utils/BrowserView'
import theme from '../../utils/theme'
import { filterObject } from '../../utils/helpers'
import ActiveBets from './ActiveBets'
import EmptyState from '../EmptyState'
import PromoList from '../PromoList'

const BetSlip = styled.div`
  ${props =>
    props.position === 'fixed' &&
    css`
      position: fixed;
      right: 0;
      bottom: 0;
      top: 64px;
      width: 320px;
      border-left: 1px solid rgba(0, 0, 0, 0.14);
    `}

  background-color: ${props => props.theme.palette.background.default};


  ${theme.breakpoints.down('sm')} {
    padding-bottom: 160px;
  }
`
const BetslipTabs = styled(Tabs)`
  background-color: ${props => props.theme.palette.background.paper};
`
const BetslipTab = styled(Tab)`
  &.MuiTab-root {
    min-width: 50%;
  }
`
const BetslipTabPanel = styled(TabPanels)`
  ${theme.breakpoints.up('sm')} {
    height: calc(100% - 48px);
    overflow-y: auto;
  }
`
const ClearAll = styled(Button)`
  margin: -${props => props.theme.spacing(2)}px;
  margin-left: auto;
`

const Betslip = props => {
  // Position required to use in full mobile betslip
  const { position, pageContext } = props
  // Some local state to handle messages snackbar opening/closing
  // its message and some bets to keep
  const [messageHub, setMessages] = useState({
    open: false,
    message: '',
    success: false,
    variant: '',
    keepBets: {},
  })
  // Some local state to handle multi bets. Currently only set up
  // for straight multi bets
  const [multiBets, setMultiBets] = useState({
    straight: {},
  })

  // create a timer variable to simulate loading
  const timer = useRef()

  // Cleanup the timer used for simulated loading
  useEffect(
    () => () => {
      clearTimeout(timer.current)
    },
    []
  )

  // Tab state
  const [value, setValue] = useState(0)
  // Handle tab change
  const handleChange = (event, newValue) => {
    setValue(newValue)
  }

  function a11yProps(index) {
    return {
      id: `betslip-${index}`,
      'aria-controls': `betslip-panel-${index}`,
    }
  }

  // Handle close of message snackbar
  const handleMessageClose = () => {
    setMessages({
      ...messageHub,
      open: false,
    })
  }

  return (
    <ContextConsumer>
      {({ data, set }) => {
        const betslipIds = Object.keys(data.betslip)
        // check if there is bet history
        const history = { ...data.user.history }
        // Extract out the open bets - later on check for win and lose
        const openBets = filterObject(history, 'result', 'cashed-out')
        // Get the keys in order to count
        const openBetsIds = Object.keys(openBets)

        // check if user is logged in
        const isUserLoggedIn = !_.isEmpty(data.user)
        const userBalance = parseFloat(data.user.balance)

        const handlePlaceBet = totalStake => {
          // Update messageHub state via a loading simulation
          setMessages({
            ...messageHub,
            open: true,
            variant: 'loading',
            message: 'Placing bets...',
          })

          if (totalStake <= userBalance) {
            timer.current = setTimeout(() => {
              const betsPlaced = addToBetHistory(
                betslipIds,
                data,
                set,
                multiBets,
                setMultiBets
              )

              // MessageToast
              setMessages({
                ...messageHub,
                open: true,
                message: `Bet Placed. Receipt #${getReceiptNumber()}`,
                variant: 'success',
                keepBets: betsPlaced,
              })
            }, 2000)

            // Update the user's balance
            updateUserBalance(data, set, totalStake)
            // Add bets to history
            // clear betslip - if it has valid stake
            // when clicking keep bets, return the last bet in bet history
          } else {
            timer.current = setTimeout(() => {
              setMessages({
                ...messageHub,
                open: true,
                message: 'Insufficient funds',
                variant: 'error',
              })
            }, 2000)
          }
        }

        const handleKeepBets = () => {
          const betslip = { ...data.betslip }

          Object.keys(messageHub.keepBets).forEach(key => {
            const bet = messageHub.keepBets[key]

            betslip[`${bet.id}-${bet.marketIndex}-${bet.buttonIndex}`] = {
              id: bet.id,
              marketIndex: bet.marketIndex,
              buttonIndex: bet.buttonIndex,
              sport: bet.sport,
              competition: bet.competition,
              marketTitle: bet.marketTitle,
              name: bet.name,
              odds: bet.odds,
              slug: bet.slug,
              date: bet.date,
              start: bet.start,
            }

            set({ betslip })
            // close the message snackbar
            setMessages({
              ...messageHub,
              open: false,
            })
          })
        }

        return (
          <BetSlip position={position}>
            <>
              <BrowserView>
                <BetslipTabs
                  value={value}
                  onChange={handleChange}
                  indicatorColor="primary"
                  textColor="primary"
                  aria-label="full width tabs example"
                  variant={!isUserLoggedIn ? `fullWidth` : null}
                >
                  <BetslipTab
                    label={
                      betslipIds.length > 0
                        ? `Bet Slip [${betslipIds.length}]`
                        : `Bet Slip`
                    }
                    {...a11yProps(0)}
                  />
                  {isUserLoggedIn && (
                    <BetslipTab
                      label={
                        openBetsIds.length > 0
                          ? `Open [${openBetsIds.length}]`
                          : `Open`
                      }
                      {...a11yProps(2)}
                    />
                  )}
                </BetslipTabs>
              </BrowserView>

              <BetslipTabPanel value={value} index={0} page="betslip">
                {/* Render out the contents of context bets  */}
                {!betslipIds.length && (
                  <>
                    <EmptyState
                      key="empty-betslip"
                      section="receipt"
                      title="Your bet slip is empty"
                      text="Check out today's sports to place a bet"
                    />
                    <BrowserView>
                      <PromoList pageContext={pageContext} />
                    </BrowserView>
                  </>
                )}
                {!!betslipIds.length && (
                  <>
                    <Box display="flex" alignItems="center" pt={2} px={4}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Single Bets ({betslipIds.length})
                      </Typography>
                      <ClearAll
                        color="primary"
                        onClick={() => removeAllFromSlip(set)}
                      >
                        Clear All
                      </ClearAll>
                    </Box>
                    <ActiveBets
                      data={data}
                      set={set}
                      disabled={messageHub.variant === 'loading'}
                    />
                  </>
                )}
                {betslipIds.length >= 2 && (
                  <>
                    <Box display="flex" alignItems="center" px={4}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Multiples
                      </Typography>
                    </Box>
                    <Multiples
                      bets={data.betslip}
                      stake={0}
                      multiBets={multiBets}
                      setMultiBets={setMultiBets}
                      disabled={messageHub.variant === 'loading'}
                    />
                  </>
                )}
                {!!betslipIds.length && (
                  <BetTotals
                    bets={data.betslip}
                    multiBets={multiBets}
                    loading={messageHub.variant === 'loading'}
                    isUserLoggedIn={isUserLoggedIn}
                    userBalance={userBalance}
                    handlePlaceBet={handlePlaceBet}
                  />
                )}
              </BetslipTabPanel>
              <BetslipTabPanel value={value} index={1} page="betslip">
                {openBets && <OpenBets bets={openBets} />}
              </BetslipTabPanel>
            </>

            <MessageSnackBar
              messageHub={messageHub}
              onClose={handleMessageClose}
              onSuccess={handleKeepBets}
            />
          </BetSlip>
        )
      }}
    </ContextConsumer>
  )
}

Betslip.propTypes = {
  position: PropTypes.string,
  pageContext: PropTypes.object,
}

export default Betslip
